import { useLazyQuery, useMutation } from "@apollo/client";
import { Alert, Box, Grid } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  GetWasteContainerQuery,
  Issue,
  IssueLevel,
  LogContainerFillEventInput,
  UnitType,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { getDateLikeIsoDate, toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  CONTAINER_MUTATION_REFETCH_QUERIES,
  LOG_CONTAINER_FILL_EVENT_MUTATION,
  UPDATE_CONTAINER_FILL_EVENT_MUTATION,
  VALIDATE_CONTAINER_FILL_EVENT_INPUT,
} from "./api";
import {
  formatContainerDate,
  isLocalDateInFuture,
  isOutsideEpisodicWindow,
  OCCURRED_AT_SHORTCUTS,
  UNIT_SELECT_ITEMS,
} from "./containerDisplay";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;
type ContainerFillEvent = WasteContainerDetail["fillEvents"][number];
type ContainerEpisodicEvent = NonNullable<WasteContainerDetail["episodicEvent"]>;

/** "06/01/2026 – 07/31/2026" — the window every episodic guard cites. */
function episodicWindowLabel(event: ContainerEpisodicEvent): string {
  return `${formatContainerDate(event.startDate)} – ${formatContainerDate(event.accumulationDeadline)}`;
}

type FillEventFormValues = {
  direction: "ADD" | "REMOVE";
  amount: string;
  unit: UnitType | null;
  occurredAt: string | null;
  isEstimate: boolean;
  // Null once the field is cleared (the text field coerces "" to null).
  note: string | null;
};

const DIRECTION_OPTIONS = [
  { value: "ADD", label: "Add waste" },
  { value: "REMOVE", label: "Remove waste" },
];

function localIssue(key: string, message: string): Issue {
  return {
    level: IssueLevel.Critical,
    key,
    message,
    modelName: "WasteContainer",
    dataName: null,
    jurisdictions: [],
  };
}

/**
 * Maps the form onto the signed mutation input: the direction radio carries
 * the sign, the amount field carries the magnitude. Null when the form is
 * too incomplete to express an input at all.
 */
function toFillEventInput(
  values: FillEventFormValues,
  containerId: string
): LogContainerFillEventInput | null {
  const magnitude = safeParseFloat(values.amount);
  if (magnitude === null || !values.unit || !values.occurredAt) {
    return null;
  }
  const occurredAt = toUtcMidnightIsoFromDateLike(DateTime.fromISO(values.occurredAt));
  if (!occurredAt) {
    return null;
  }
  return {
    containerId,
    amount: values.direction === "REMOVE" ? -Math.abs(magnitude) : Math.abs(magnitude),
    unit: values.unit,
    isEstimate: values.isEstimate,
    occurredAt,
    // The note field yields null once cleared (not ""), so guard the trim.
    note: values.note?.trim() || undefined,
  };
}

function incompleteFormIssues(values: FillEventFormValues): Issue[] {
  const issues: Issue[] = [];
  if (safeParseFloat(values.amount) === null) {
    issues.push(localIssue("amount", "Enter the amount"));
  }
  if (!values.unit) {
    issues.push(localIssue("unit", "Select a unit"));
  }
  if (!values.occurredAt) {
    issues.push(localIssue("occurredAt", "Enter the date this happened"));
  }
  return issues;
}

/**
 * An existing event's own sign is the truth about which way it went; only a
 * brand-new event takes the caller's requested direction.
 */
function initialFillDirection(
  fillEvent: ContainerFillEvent | undefined,
  requested: "ADD" | "REMOVE"
): "ADD" | "REMOVE" {
  if (!fillEvent) {
    return requested;
  }
  return fillEvent.amount < 0 ? "REMOVE" : "ADD";
}

export type ContainerFillEventDialogProps = {
  container: WasteContainerDetail;
  /** Present when editing an existing event. */
  fillEvent?: ContainerFillEvent;
  /**
   * Which way the form opens when logging a new event. Defaults to adding,
   * the common case; the drawer's "Log removal" action opens it the other way
   * so the user isn't asked to re-pick what they just clicked. Ignored when
   * editing, where the stored event's own sign decides.
   */
  initialDirection?: "ADD" | "REMOVE";
  onClose: () => void;
};

/**
 * The form's own completeness rules, then the server's. The future-date guard
 * runs here rather than server-side: `occurredAt` goes over the wire as a
 * UTC-midnight, date-only value, so only the client knows the reader's day.
 */
function useFillEventValidation(
  containerId: string,
  currentFillEventId: string | undefined,
  episodicEvent: ContainerEpisodicEvent | null
) {
  const [validateQuery] = useLazyQuery(VALIDATE_CONTAINER_FILL_EVENT_INPUT, {
    fetchPolicy: "no-cache",
  });
  return useCallback(
    async (values: FillEventFormValues): Promise<Issue[]> => {
      const input = toFillEventInput(values, containerId);
      if (!input) {
        return incompleteFormIssues(values);
      }
      if (isLocalDateInFuture(values.occurredAt)) {
        return [localIssue("occurredAt", "The date can't be in the future")];
      }
      // The client face of the server's fill-window guard: adds on an
      // event's container must land inside the window; removals may not.
      if (
        input.amount > 0 &&
        episodicEvent &&
        values.occurredAt &&
        isOutsideEpisodicWindow(
          values.occurredAt,
          episodicEvent.startDate,
          episodicEvent.accumulationDeadline
        )
      ) {
        return [
          localIssue(
            "occurredAt",
            `Waste can only be added within the episodic event's window (${episodicWindowLabel(episodicEvent)})`
          ),
        ];
      }
      const { data } = await validateQuery({
        variables: { input, currentFillEventId },
      });
      return data?.containerFillEventInputValidator ?? [];
    },
    [validateQuery, containerId, currentFillEventId, episodicEvent]
  );
}

/**
 * What the form opens with. A new event defaults to today and to the
 * container's own unit — the capture-first common case; an edit mirrors the
 * stored event, reading its date as a plain calendar day so a
 * negative-UTC-offset reader isn't shown the day before.
 */
function initialFillEventValues(
  container: WasteContainerDetail,
  fillEvent: ContainerFillEvent | undefined,
  initialDirection: "ADD" | "REMOVE"
): FillEventFormValues {
  return {
    direction: initialFillDirection(fillEvent, initialDirection),
    amount: fillEvent ? String(Math.abs(fillEvent.amount)) : "",
    unit: fillEvent?.unit ?? container.amountUnit ?? container.capacityUnit ?? null,
    occurredAt: fillEvent?.occurredAt
      ? (getDateLikeIsoDate(fillEvent.occurredAt) ?? null)
      : DateTime.local().toISODate(),
    isEstimate: fillEvent?.isEstimate ?? container.isEstimate,
    note: fillEvent?.note ?? "",
  };
}

export function ContainerFillEventDialog({
  container,
  fillEvent,
  initialDirection = "ADD",
  onClose,
}: ContainerFillEventDialogProps) {
  const alerts = useAlerts();
  const mode: "log" | "edit" = fillEvent ? "edit" : "log";

  const defaultValues = useMemo<FillEventFormValues>(
    () => initialFillEventValues(container, fillEvent, initialDirection),
    [container, fillEvent, initialDirection]
  );

  const validateInput = useFillEventValidation(
    container.id,
    fillEvent?.id,
    container.episodicEvent ?? null
  );

  const form = useValidatingForm<FillEventFormValues>(
    defaultValues,
    // Seed with the container's standing issues (over capacity, below-capacity
    // FULL) from the query that already loaded it, so an edit opens showing
    // them rather than only after the first change. A blank log form isn't
    // seeded — its own nags would just fire for fields the user hasn't
    // reached — so issues are seeded only when editing an existing event.
    mode === "edit" ? container.issues : [],
    validateInput,
    undefined,
    {
      // A background refetch mid-edit re-issues the defaults; keep what the
      // user has typed rather than resetting under their cursor.
      keepDirtyValuesOnReset: true,
    }
  );
  const {
    getValues,
    issues,
    formState: { isDirty, dirtyFields },
  } = form;

  const [logFillEvent, { loading: isLogging }] = useMutation(LOG_CONTAINER_FILL_EVENT_MUTATION, {
    refetchQueries: CONTAINER_MUTATION_REFETCH_QUERIES,
  });
  const [updateFillEvent, { loading: isUpdating }] = useMutation(
    UPDATE_CONTAINER_FILL_EVENT_MUTATION,
    {
      refetchQueries: CONTAINER_MUTATION_REFETCH_QUERIES,
    }
  );

  // Both mutations report the same way; only the words differ.
  const saveHandlers = (success: string, verb: string) => ({
    onCompleted: () => {
      alerts.success(success);
      onClose();
    },
    onError: (error: Error) => {
      alerts.error(error.message ?? `An error occurred while ${verb} the fill event`);
    },
  });

  const onSubmit = async () => {
    const values = getValues();
    const input = toFillEventInput(values, container.id);
    if (!input) {
      alerts.error("Enter an amount, unit, and date");
      return;
    }
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before saving");
      return;
    }
    if (mode === "log") {
      await logFillEvent({ variables: { input }, ...saveHandlers("Fill event logged", "logging") });
      return;
    }
    if (!fillEvent) {
      return;
    }
    await updateFillEvent({
      variables: {
        input: {
          id: fillEvent.id,
          amount: input.amount,
          unit: input.unit,
          isEstimate: values.isEstimate,
          // Only send the date when the user actually touched it, so an
          // untouched value can never round-trip through the local zone.
          occurredAt: dirtyFields.occurredAt ? input.occurredAt : undefined,
          note: values.note?.trim() || null,
        },
      },
      ...saveHandlers("Fill event updated", "updating"),
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title={mode === "log" ? "Log fill event" : "Edit fill event"}
      primaryActionButtonProps={{
        loading: isLogging || isUpdating,
        onClick: onSubmit,
        disabled: hasCriticalIssues(issues) || !isDirty,
      }}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
    >
      <FillEventFields form={form} episodicEvent={container.episodicEvent ?? null} />
    </Dialog>
  );
}

/**
 * Which way the waste moved, how much, when, and why. Split from the dialog
 * shell, which owns the two mutations and the save gate.
 */
function FillEventFields({
  form,
  episodicEvent,
}: {
  form: ReturnType<typeof useValidatingForm<FillEventFormValues>>;
  episodicEvent: ContainerEpisodicEvent | null;
}) {
  // Adds are window-bound on an event's container; removals aren't, so the
  // explainer and the date bounds follow the direction radio live.
  const direction = form.watch("direction");
  const windowBound = episodicEvent !== null && direction === "ADD";
  return (
    <FormProvider {...form}>
      <Box component="form" sx={{ pt: 1 }}>
        <Grid container spacing={2}>
          {windowBound && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                This container accumulates under the episodic event &quot;{episodicEvent.reason}
                &quot; — waste can only be added within the event&apos;s window,{" "}
                {episodicWindowLabel(episodicEvent)}. Removals aren&apos;t restricted.
              </Alert>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <RadioGroupField
              name="direction"
              label="Direction"
              control={form.control}
              radioOptions={DIRECTION_OPTIONS}
            />
          </Grid>
          <Grid size={{ xs: 7 }}>
            <FormTextField
              name="amount"
              label="Amount"
              control={form.control}
              type="number"
              textFieldProps={{ required: true }}
            />
          </Grid>
          <Grid size={{ xs: 5 }}>
            <FormSelect
              name="unit"
              label="Unit"
              control={form.control}
              selectItems={UNIT_SELECT_ITEMS}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <DateField
              name="occurredAt"
              label="Date"
              control={form.control}
              required
              shortcuts={OCCURRED_AT_SHORTCUTS}
              datePickerProps={{
                disableFuture: true,
                // Adds are locked to the event's window; the validator still
                // catches a typed-in date the picker couldn't stop.
                ...(windowBound && episodicEvent
                  ? {
                      minDate: DateTime.fromISO(episodicEvent.startDate),
                      maxDate: DateTime.fromISO(episodicEvent.accumulationDeadline),
                    }
                  : {}),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormTextField
              name="note"
              label="Note"
              control={form.control}
              textFieldProps={{ multiline: true }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <CheckboxField
              name="isEstimate"
              label="This amount is an estimate"
              control={form.control}
              noErrorDisplay
            />
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  );
}
